<template>
  <div
    class="tw-flex tw-flex-col tw-gap-6"
    data-testid="assistant-conversation"
  >
    <div
      v-for="message in messages"
      :key="message.id"
      class="tw-flex tw-flex-col tw-gap-2"
    >
      <div
        v-if="message.role === 'user'"
        class="tw-flex tw-justify-end tw-pl-8"
      >
        <div
          class="
            assistant-message--user
            tw-rounded-lg
            tw-bg-sf-slate-subtle-2
            tw-p-2
            tw-text-sm
            tw-leading-5
            tw-text-sf-slate-darker-1
            tw-whitespace-pre-wrap
            tw-break-words
          "
        >
          {{ message.text }}
        </div>
      </div>

      <!-- Assistant reply: full-width markdown, optional pills + rewind -->
      <div v-else class="assistant-reply tw-group tw-flex tw-flex-col tw-gap-2">
        <div
          v-dompurify-html="renderMarkdown(message)"
          class="
            assistant-message--assistant
            tw-text-sm tw-text-sf-slate-darker-1
          "
        ></div>

        <AssistantSuggestionPills
          v-if="
            message.id === lastAssistantMessageId && message.suggestions.length
          "
          :suggestions="message.suggestions"
          variant="reply"
          @select="submitSuggestion"
        />

        <div class="tw-flex">
          <button
            v-tooltip.placementBottom="
              I18n.t('companies_overview.assistant.rewind_tooltip')
            "
            type="button"
            class="
              assistant-rewind
              tw-flex
              tw-items-center
              tw-gap-1
              tw-rounded-lg
              tw-border-0
              tw-bg-sf-slate-subtle-2
              tw-px-2
              tw-py-0.5
              tw-text-xs
              tw-text-sf-slate-dark-1
              tw-cursor-pointer
            "
            @click="rewindTo(message.id)"
          >
            <i class="fa fa-solid fa-clock-rotate-left tw-text-[10px]" />
            {{ I18n.t('companies_overview.assistant.rewind') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Loading row shown while waiting for the assistant's reply; once reply
         text starts streaming in, it renders in place of the loading label. -->
    <div v-if="loading" class="tw-flex tw-flex-col tw-gap-2">
      <div
        v-if="streamingText"
        v-dompurify-html="streamingHtml"
        class="
          assistant-message--assistant
          tw-text-sm tw-text-sf-slate-darker-1
        "
        data-testid="assistant-streaming"
      ></div>
      <div
        role="status"
        aria-live="polite"
        class="
          tw-flex tw-items-center tw-gap-2 tw-text-sm tw-text-sf-slate-light-2
        "
        data-testid="assistant-loading"
      >
        <div class="sf-spinner sf-inline-block"></div>
        <span>{{ workingLabel }}</span>
        <span
          class="tw-ml-auto tw-tabular-nums tw-text-sf-slate-light-2"
          data-testid="assistant-elapsed"
        >
          {{ elapsedLabel }}
        </span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onUnmounted } from 'vue'
import { storeToRefs } from 'pinia'
import { marked } from 'marked'
import I18n from '@app/adapters/I18n'
import type { AssistantMessage } from '@app/insights/store/assistant'
import { useAssistantStore } from '@app/insights/store/assistant'
import { useElapsedTimer } from '@app/shared/vue/composables/use-elapsed-timer'
import AssistantSuggestionPills from './assistant-suggestion-pills.vue'

const assistantStore = useAssistantStore()
const {
  messages,
  loading,
  streamingText,
  currentTool,
  lastAssistantMessageId,
} = storeToRefs(assistantStore)
const { submitSuggestion, rewindTo } = assistantStore

const workingLabel = computed((): string => {
  const loadingLabel = I18n.t('companies_overview.assistant.loading')
  if (!currentTool.value) {
    return loadingLabel
  }

  return I18n.t(`companies_overview.assistant.tools.${currentTool.value}`, {
    defaultValue: loadingLabel,
  })
})

// Elapsed timer for the running turn, shown next to the working label.
const { elapsedLabel } = useElapsedTimer(loading)

const htmlCache = new Map<string, string>()
onUnmounted(() => htmlCache.clear())

function renderMarkdown(message: AssistantMessage): string {
  const cached = htmlCache.get(message.id)
  if (cached !== undefined) return cached

  const parsed = marked.parse(message.text, { async: false })
  const html = typeof parsed === 'string' ? parsed : ''
  htmlCache.set(message.id, html)
  return html
}

// Computed (not a template method) so the cumulative streaming buffer is re-parsed only when
// streamingText actually changes, not on every unrelated re-render (e.g. the 1x/sec elapsed tick).
const streamingHtml = computed((): string => {
  const parsed = marked.parse(streamingText.value, { async: false })
  return typeof parsed === 'string' ? parsed : ''
})
</script>

<style scoped lang="scss">
@import 'silverfin-ui-kit/src/scss/tooling';

/* Rewind affordance is revealed on hover/focus of the reply (matches design). */
.assistant-rewind {
  opacity: 0;
  transition: opacity 0.15s ease;
}

.assistant-rewind:focus-visible {
  opacity: 1;
}

.assistant-reply:hover .assistant-rewind {
  opacity: 1;
}

/* The global silverfin-ui-kit reset zeroes margins, font styling and list
   markers on every element, so the raw HTML produced by `marked` renders as
   flat unstyled text. Restore the elements markdown can emit, scoped to the
   reply so nothing leaks out. */
.assistant-message--assistant {
  :deep(p) {
    margin: 0 0 0.5em;
  }

  :deep(p:last-child) {
    margin-bottom: 0;
  }

  :deep(h1),
  :deep(h2),
  :deep(h3),
  :deep(h4),
  :deep(h5),
  :deep(h6) {
    margin: 0.75em 0 0.35em;
    font-weight: 600;
    line-height: 1.3;
  }

  :deep(:first-child) {
    margin-top: 0;
  }

  :deep(h1) {
    font-size: 1.3em;
  }

  :deep(h2) {
    font-size: 1.15em;
  }

  :deep(h3) {
    font-size: 1.05em;
  }

  :deep(h4),
  :deep(h5),
  :deep(h6) {
    font-size: 1em;
  }

  :deep(strong),
  :deep(b) {
    font-weight: 600;
  }

  :deep(em),
  :deep(i) {
    font-style: italic;
  }

  :deep(a) {
    color: -color('sky', 'dark');
    text-decoration: underline;

    &:hover {
      color: -color('sky', 'darker');
    }
  }

  :deep(ul),
  :deep(ol) {
    margin: 0 0 0.5em;
    padding-left: 1.25em;
  }

  :deep(ul) {
    list-style-type: disc;
  }

  :deep(ol) {
    list-style-type: decimal;
  }

  :deep(li) {
    margin-bottom: 0.15em;
  }

  :deep(li > ul),
  :deep(li > ol) {
    margin-bottom: 0;
  }

  :deep(code) {
    padding: 0.1em 0.3em;
    border-radius: 3px;
    background-color: theme('colors.sf-slate.subtle-2');
    font-family: monospace;
    font-size: 0.85em;
  }

  :deep(pre) {
    margin: 0 0 0.5em;
    padding: 0.6em 0.75em;
    border-radius: 6px;
    background-color: theme('colors.sf-slate.subtle-2');
    overflow-x: auto;

    code {
      padding: 0;
      background-color: transparent;
      font-size: inherit;
    }
  }

  :deep(blockquote) {
    margin: 0 0 0.5em;
    padding-left: 0.75em;
    border-left: 3px solid theme('colors.sf-slate.lighter-1');
    color: theme('colors.sf-slate.dark-1');
  }

  :deep(hr) {
    margin: 0.75em 0;
    border: 0;
    border-top: 1px solid theme('colors.sf-slate.lighter-1');
  }

  :deep(table) {
    margin: 0 0 0.5em;
    border-collapse: collapse;
  }

  :deep(th),
  :deep(td) {
    padding: 0.3em 0.5em;
    border: 1px solid theme('colors.sf-slate.lighter-1');
    text-align: left;
  }

  :deep(th) {
    background-color: theme('colors.sf-slate.subtle-2');
    font-weight: 600;
  }
}
</style>
